import type { GetApiV1ThreeLetterCodeClientAdminsPartnerFeatureFlags200 } from '@fukuri-admins/apis/client/gen/model';
import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { downloadFile, updateSearchParam } from '@fukuri-admins/utils/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetClientAdminsPartnerFeatureFlags } from '~/hooks';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '~/types/table/ActionTableSortParamsType';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import { PendingUserPresentation } from '../presentations';
import { useGetClientUsers } from './useGetClientUsers';
import { useGetClientUsersCsv } from './useGetClientUsersCsv';
import { usePostClientUserLoginReminder } from './usePostClientUserLoginReminder';
import { usePostClientUsersBulkLoginReminder } from './usePostClientUsersBulkLoginReminder';

type PresentationProps = React.ComponentProps<typeof PendingUserPresentation>;
type ClientUsers = ReturnType<typeof useGetClientUsers>['data']['users'];

type IUsePendingUser = () => {
  state: {
    users: ClientUsers;
    sortParams: ActionTableSortParamsType;
    keyword: string;
    currentPage: PresentationProps['currentPage'];
    totalPage: PresentationProps['totalPage'];
    totalCount: PresentationProps['totalCount'];
    totalPendingCount: PresentationProps['totalPendingCount'];
    partnerFeatureFlagData: GetApiV1ThreeLetterCodeClientAdminsPartnerFeatureFlags200;
  };
  actions: {
    handleKeywordChange: PresentationProps['handleKeywordChange'];
    handleSearch: PresentationProps['handleSearch'];
    handlePageChange: PresentationProps['handlePageChange'];
    handleOutputCsv: PresentationProps['handleOutputCsv'];
    handleHeaderSort: PresentationProps['handleHeaderSort'];
    onClickRow: PresentationProps['onClickRow'];
    onClickSendReminder: PresentationProps['onClickSendReminder'];
    onOpenBulkConfirm: PresentationProps['onOpenBulkConfirm'];
  };
};

export const usePendingUser: IUsePendingUser = () => {
  const threeLetterCode = getThreeLetterCode() || '';
  const { showToast } = useToast();
  const { showAlertDialog } = useAlertDialog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // API連携パートナーには一覧の行クリックによる /users/:id への遷移を提供しない（判定は converter に委譲）
  // （screens/PendingUser が Suspense 境界を持つため、ここでは追加の Suspense ラップは不要）
  const { data: partnerFeatureFlagData } = useGetClientAdminsPartnerFeatureFlags({
    threeLetterCode,
  });
  // 負値・小数・不正値（NaN）は 1 に丸める
  const pageParam = Number.parseInt(searchParams.get('page') ?? '', 10);
  const currentPage = pageParam >= 1 ? pageParam : 1;

  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>(
    defaultActionTableSortParams
  );

  // 確定済みキーワードは URL params で管理。入力中（未確定）のみ state で保持
  const appliedKeyword = searchParams.get('keyword') ?? '';
  const [keyword, setKeyword] = useState(appliedKeyword);

  // 初回ログイン未完了者の一覧（ページング・件数はヘッダから取得）
  const {
    data,
    totalPage,
    totalCount: totalPendingCount,
  } = useGetClientUsers({
    threeLetterCode,
    f: 'pending',
    keyword: appliedKeyword,
    page: currentPage,
  });

  // 未完了率の分母となる全ユーザー数（フィルタなし）
  const { totalCount } = useGetClientUsers({ threeLetterCode });

  const { trigger: triggerBulkReminder } = usePostClientUsersBulkLoginReminder(threeLetterCode, {
    onSuccess: () => showToast({ message: 'メールの送信を受け付けました。', status: 'success' }),
    onError: () => showToast({ message: '再招待メールの送信に失敗しました。', status: 'danger' }),
  });

  const { trigger: triggerSendReminder } = usePostClientUserLoginReminder(threeLetterCode, {
    onSuccess: () => showToast({ message: 'メールを送信しました。', status: 'success' }),
    onError: () => showToast({ message: '再招待メールの送信に失敗しました。', status: 'danger' }),
  });

  const { trigger: triggerDownloadCsv } = useGetClientUsersCsv(threeLetterCode, {
    onSuccess: blob => downloadFile(blob, 'pending_users.csv'),
  });

  const handleOutputCsv = async () => {
    await triggerDownloadCsv();
  };

  const handleKeywordChange = (value: string) => setKeyword(value);

  const handleSearch = () => {
    const withKeyword = updateSearchParam(searchParams, 'keyword', keyword);
    setSearchParams(updateSearchParam(withKeyword, 'page', '1'));
  };

  const handlePageChange = (page: number) => {
    setSearchParams(updateSearchParam(searchParams, 'page', page.toString()));
  };

  // クリックされた列でソートする。同じ列の再クリックで昇順/降順を反転する
  const handleHeaderSort = (column: string) =>
    setSortParams(prev => ({
      column,
      isAsc: prev.column === column ? !prev.isAsc : false,
    }));

  const onClickRow = (userId: string) =>
    navigate(replaceRouteParams(ROUTES.USER_SHOW, { id: Number(userId) }));

  const onOpenBulkConfirm = () => {
    showAlertDialog({
      title: '再招待メールの一括送信',
      message: `${totalPendingCount}名の利用者に再招待メールを送信します。`,
      confirmText: '送信',
      onConfirm: () => {
        triggerBulkReminder();
      },
    });
  };

  const onClickSendReminder = (userId: string, fullName: string) => {
    showAlertDialog({
      title: '再招待メールの送信',
      message: `${fullName}さんに再招待メールを送信します。`,
      confirmText: '送信',
      onConfirm: async () => {
        await triggerSendReminder(Number(userId));
      },
    });
  };

  return {
    state: {
      users: data.users,
      sortParams,
      keyword,
      currentPage,
      totalPage,
      totalCount,
      totalPendingCount,
      partnerFeatureFlagData,
    },
    actions: {
      handleKeywordChange,
      handleSearch,
      handlePageChange,
      handleOutputCsv,
      handleHeaderSort,
      onClickRow,
      onClickSendReminder,
      onOpenBulkConfirm,
    },
  };
};
